import { Component, input, output, computed, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IAssetable } from '../../models/assetable.model';
import { AspectType, ResolutionType, StorageImageSettings, CloudStorageData, extractPath } from '../../classes/storage.models';
import { CropperComponentModal } from '../cropper-modal/cropper-modal.component';
import { MultiImagesStorageService } from '../../services/multi-images-storage.service';
import { SimpleUploaderComponent } from '../simple-uploader/simple-uploader.component';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';

@Component({
  selector: 'assets-loader',
  templateUrl: './assets-loader.html',
  styleUrls: ['./assets-loader.scss'],
  standalone: true,
  imports: [CommonModule, CropperComponentModal, SimpleUploaderComponent, ButtonModule, MessageModule],
})
export class AssetsLoaderComponent implements OnDestroy {
  assets = input.required<IAssetable>();
  storagePath = input.required<string>();
  assetsChange = output<IAssetable>();
  assetUpdate = output<{ assets: Partial<IAssetable> }>();
  onFileSelected = output<File>();

  private storageService = inject(MultiImagesStorageService);
  private reverseInterval: any;

  public bannerImgSettings = computed<StorageImageSettings>(() => ({
    path: this.storagePath(),
    fileName: undefined,
    cropSettings: { aspectRatio: AspectType.Rectangle, resolutions: [ResolutionType.MediumLarge], resizeToWidth: 700 },
  }));

  public imageStorageSettings = computed<StorageImageSettings>(() => ({
    path: this.storagePath(),
    fileName: undefined,
    cropSettings: { aspectRatio: AspectType.Vertical_2_3, resolutions: [ResolutionType.MediumLarge], resizeToWidth: 500 },
  }));

  public stickerStorageSettings = computed<StorageImageSettings>(() => ({
    path: `${this.storagePath()}/stickers`,
    fileName: undefined,
    cropSettings: { aspectRatio: AspectType.Square, resolutions: [ResolutionType.MediumLarge], resizeToWidth: 400 },
  }));

  public onImageUploaded(event: CloudStorageData, type: 'image' | 'banner' | 'sticker' = 'image') {
    const currentAssets = this.assets() ?? { image: null, banner: null, stickers: [] };
    let updatedField: Partial<IAssetable>;

    switch (type) {
      case 'image':
        updatedField = { image: event };
        break;
      case 'banner':
        updatedField = { banner: event };
        break;
      case 'sticker':
        updatedField = { stickers: [...(currentAssets.stickers || []), event] };
        break;
    }

    const newAssets = { ...currentAssets, ...updatedField };
    this.assetUpdate.emit({ assets: updatedField });
    this.assetsChange.emit(newAssets);
  }

  public onMotionUploaded(event: CloudStorageData) {
    if (!event.url) return;

    const currentMotionUrl = this.assets()?.motion?.url;
    if (currentMotionUrl) {
      this.storageService.deleteImage(currentMotionUrl);
    }

    const updatedField = { motion: { url: event.url } };
    const newAssets = { ...this.assets(), ...updatedField };

    // Emit only the update field
    this.assetUpdate.emit({ assets: updatedField });
    // Assets Changes emit the new object
    this.assetsChange.emit(newAssets);
  }

  public onMotionAdded(event: CloudStorageData) {
    const currentAssets = this.assets() ?? { motions: [] };
    const updatedField = { motions: [...(currentAssets.motions || []), event] };
    const newAssets = { ...currentAssets, ...updatedField };
    this.assetUpdate.emit({ assets: updatedField });
    this.assetsChange.emit(newAssets);
  }

  public removeSticker(sticker: CloudStorageData) {
    const path = extractPath(sticker);
    if (path) {
      this.storageService.deleteImage(path);
    }

    const currentStickers = this.assets()?.stickers ?? [];
    const updatedStickers = currentStickers.filter((s) => s.url !== sticker.url);
    const updatedField = { stickers: updatedStickers };
    const newAssets = { ...this.assets(), ...updatedField };

    this.assetUpdate.emit({ assets: updatedField });
    this.assetsChange.emit(newAssets);
  }

  public removeMotion(motion: CloudStorageData) {
    const path = extractPath(motion);
    if (path) {
      this.storageService.deleteImage(path);
    }

    const currentMotions = this.assets()?.motions ?? [];
    const updatedMotions = currentMotions.filter((m) => m.url !== motion.url);
    const updatedField = { motions: updatedMotions };
    const newAssets = { ...this.assets(), ...updatedField };

    this.assetUpdate.emit({ assets: updatedField });
    this.assetsChange.emit(newAssets);
  }

  public removeMainMotion() {
    const motionToRemove = this.assets()?.motion;
    if (motionToRemove?.url) {
      const path = extractPath(motionToRemove);
      if (path) {
        this.storageService.deleteImage(path);
      }

      const updatedField = { motion: null };
      const newAssets = { ...this.assets(), motion: null };

      this.assetUpdate.emit({ assets: updatedField });
      this.assetsChange.emit(newAssets);
    }
  }

  public onVideoEnded(video: HTMLVideoElement) {
    video.pause();
    this.reverseInterval = setInterval(() => {
      if (video.currentTime <= 0) {
        clearInterval(this.reverseInterval);
        video.play();
      } else {
        video.currentTime -= 0.03;
      }
    }, 30);
  }

  ngOnDestroy() {
    if (this.reverseInterval) {
      clearInterval(this.reverseInterval);
    }
  }
}
